import { Dispatch, SetStateAction, useState } from "react";
import { IonSegment, IonSegmentButton } from "@ionic/react";
import { UseMutateFunction } from "@tanstack/react-query";
import { DateTime } from "luxon";
import { SchoolHoursModel, SchoolYearType } from "model";
import { DisabledDateRanges } from "swing-components";
import { translate } from "utils/string-table";

import { isSameDate } from "~components/RequestForm/hooks";

import { Footer, ProfileCard } from "../ProfileCard";
import { currentAndNextAcademicYear } from "../SchoolClosures/utils";
import { SchoolYearField } from "./SchoolYearField";
import { calcHours } from "./utils";

export const tabSegmentStyles = {
  display: "block",
  alignSelf: "center",
  maxWidth: "100%",
  lineHeight: "22px",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  overflow: "hidden",
  fontSize: "14px",
  fontWeight: 500,
  transition: "color 0.15s linear 0s, opacity 0.15s linear 0s",
  color: "inherit",
};

type PickerType = "start" | "end";
// Type representing an ISO 8601 date string in YYYY-MM-DD format (Example: "2023-08-15")
type ISODateString = string;
type SchoolYearsProps = {
  schoolYears: Record<"currentYear" | "nextYear", SchoolYearType>;
  setSchoolYear: UseMutateFunction<unknown, Error, SchoolYearType | undefined, unknown>;
  hours: SchoolHoursModel;

  selectedTab: "currentYear" | "nextYear";
  setSelectedTab: Dispatch<SetStateAction<"currentYear" | "nextYear">>;
};

export function SchoolYearsView(props: SchoolYearsProps) {
  const { selectedTab, setSelectedTab, setSchoolYear, hours, schoolYears } = props;
  const [editingYears, setEditingYears] = useState(schoolYears);
  const academicYears = currentAndNextAcademicYear(DateTime.now());

  const [isEditing, setIsEditing] = useState(false);

  // Calculate academic year boundaries for the selected tab/school year

  const currentStartDate = editingYears[selectedTab].start;
  const currentEndDate = editingYears[selectedTab].end;

  const startDateInputDisabledDateRanges = getDisabledDateRangesForPicker("start", currentEndDate, {
    start: academicYears[selectedTab === "currentYear" ? 0 : 1].startDate,
    end: academicYears[selectedTab === "currentYear" ? 0 : 1].endDate,
  });

  const endDateInputDisabledDateRanges = getDisabledDateRangesForPicker("end", currentStartDate, {
    start: academicYears[selectedTab === "currentYear" ? 0 : 1].startDate,
    end: academicYears[selectedTab === "currentYear" ? 0 : 1].endDate,
  });

  function isFooterDisabled(): boolean {
    const currentTabStart = editingYears[selectedTab].end;
    const currentTabEnd = editingYears[selectedTab].end;

    if (!currentTabStart || !currentTabEnd) {
      return true;
    }

    const originalTabStart = schoolYears[selectedTab].start;
    const originalTabEnd = schoolYears[selectedTab].end;
    const isTabStartSame = isSameDate(
      DateTime.fromISO(originalTabStart || ""),
      DateTime.fromISO(currentStartDate || ""),
    );
    const isTabEndSame = isSameDate(
      DateTime.fromISO(originalTabEnd || ""),
      DateTime.fromISO(currentEndDate || ""),
    );

    return isTabStartSame && isTabEndSame;
  }

  function handleSubmit() {
    setIsEditing(false);
    setSchoolYear(editingYears[selectedTab]);
  }

  function handleCancel() {
    setIsEditing(false);

    setEditingYears(schoolYears);
  }

  function handleStartChange(value: string) {
    const newStartTime = calcHours({ hours, time: DateTime.fromISO(value), type: "start" });
    setEditingYears((prev) => ({
      ...prev,
      [selectedTab]: {
        end: prev[selectedTab].end,
        start: newStartTime,
      },
    }));
  }

  function handleEndChange(value: string) {
    const newEndTime = calcHours({ hours, time: DateTime.fromISO(value), type: "end" });
    setEditingYears((prev) => ({
      ...prev,
      [selectedTab]: {
        start: prev[selectedTab].start,
        end: newEndTime,
      },
    }));
  }

  return (
    <ProfileCard
      header={translate("SCHOOL_YEAR_HEADER")}
      isEditing={isEditing}
      onClick={() => setIsEditing(true)}
    >
      <p css={{ margin: 0, fontWeight: 400, fontSize: 16 }}>
        {translate("SCHOOL_YEAR_DESCRIPTION")}
      </p>

      <div css={{ marginBottom: 16 }}>
        <IonSegment
          disabled={isEditing}
          value={selectedTab}
          css={{ width: "100%", display: "flex" }}
          onIonChange={(e) => setSelectedTab(e.target.value as "currentYear" | "nextYear")}
        >
          <IonSegmentButton css={{ flex: "1 1 auto" }} value="currentYear">
            {academicYears[0].startDate.year}-{academicYears[0].endDate.year}
          </IonSegmentButton>
          <IonSegmentButton css={{ flex: "1 1 auto" }} value="nextYear">
            <div css={tabSegmentStyles}>
              {academicYears[1].startDate.year}-{academicYears[1].endDate.year}
            </div>
          </IonSegmentButton>
        </IonSegment>
      </div>

      <ul css={{ padding: 0 }}>
        <SchoolYearField
          label={translate("SCHOOL_YEAR_START_LABEL")}
          value={editingYears[selectedTab].start}
          isEditing={isEditing}
          disabledDateRanges={startDateInputDisabledDateRanges}
          onChange={handleStartChange}
        />
        <SchoolYearField
          label={translate("SCHOOL_YEAR_END_LABEL")}
          value={editingYears[selectedTab].end}
          isEditing={isEditing}
          disabledDateRanges={endDateInputDisabledDateRanges}
          onChange={handleEndChange}
        />
      </ul>

      {isEditing && (
        <Footer disabled={isFooterDisabled()} onSubmit={handleSubmit} onCancel={handleCancel} />
      )}
    </ProfileCard>
  );
}

/**
 * Calculates disabled date ranges for a date picker based on the type of picker
 * and the constraining date.
 *
 * For start date pickers, dates after the end date will be disabled.
 * For end date pickers, dates before the start date will be disabled.
 * All dates outside the academic year boundaries will be disabled.
 *
 * @param pickerType - The type of picker("start" or "end")
 * @param constrainingDate - The constraining date value of the picker type in ISO format(YYYY-MM-DD)
 * @param academicYearBoundaries - The boundaries of the academic year
 *
 * @returns An array of disabled date ranges or undefined if no constraints
 */
function getDisabledDateRangesForPicker(
  pickerType: PickerType,
  constrainingDate?: ISODateString,
  academicYearBoundaries?: { start: DateTime; end: DateTime } | null,
): DisabledDateRanges | undefined {
  const calculatedRanges: Array<{ label?: string; start?: ISODateString; end?: ISODateString }> =
    [];

  if (academicYearBoundaries) {
    calculatedRanges.push(
      {
        end: academicYearBoundaries.start.toISODate(),
        label: !constrainingDate ? translate("SCHOOL_YEAR_INPUT_DISABLED_LABEL") : undefined,
      },
      {
        start: academicYearBoundaries.end.plus({ days: 1 }).toISODate(),
        label: !constrainingDate ? translate("SCHOOL_YEAR_INPUT_DISABLED_LABEL") : undefined,
      },
    );
  }

  if (constrainingDate) {
    const constrainingDateAsDateTime = DateTime.fromISO(constrainingDate).toISODate();
    if (pickerType === "start") {
      calculatedRanges.push({
        start: constrainingDateAsDateTime,
        label: translate("SCHOOL_YEAR_START_AFTER_END_DISABLED_LABEL", {
          endDate: DateTime.fromISO(constrainingDate),
        }),
      });
    } else {
      calculatedRanges.unshift({
        end: constrainingDateAsDateTime,
        label: translate("SCHOOL_YEAR_END_BEFORE_START_DISABLED_LABEL", {
          startDate: DateTime.fromISO(constrainingDate),
        }),
      });
    }
  }

  return calculatedRanges.length > 0 ? (calculatedRanges as DisabledDateRanges) : undefined;
}
